/**
 * 361 Sleep — Section 9
 *
 * Large desktop (≥1200px): full-bleed video, GSAP pin at top top, scrub.
 * Phone + tablet (<1200px): preview + play → fullscreen immersive scrub.
 */
(function () {
  'use strict';

  var BOOTED = false;
  var SEEK_EPS = 0.033;
  var PIN_SPACER_BG = '#323222';
  var ST_CONFIGURED = false;
  var MOBILE_MQ = '(max-width: 1199px)';
  var DESKTOP_MQ = '(min-width: 1200px)';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function whenGsap(fn) {
    if (window.gsap && window.ScrollTrigger) {
      fn(window.gsap, window.ScrollTrigger);
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (window.gsap && window.ScrollTrigger) {
        clearInterval(timer);
        fn(window.gsap, window.ScrollTrigger);
      } else if (tries > 80) {
        clearInterval(timer);
      }
    }, 50);
  }

  function onceReady(video, fn) {
    if (video.readyState >= 1 && video.duration && isFinite(video.duration)) {
      fn();
      return;
    }

    var done = false;
    function finish() {
      if (done) return;
      if (!(video.duration && isFinite(video.duration))) return;
      done = true;
      video.removeEventListener('loadedmetadata', finish);
      video.removeEventListener('durationchange', finish);
      fn();
    }

    video.addEventListener('loadedmetadata', finish);
    video.addEventListener('durationchange', finish);
    try {
      video.load();
    } catch (e) {
      /* ignore */
    }
  }

  function seekVideo(video, time) {
    if (!video.duration || !isFinite(video.duration)) return;
    var next = Math.max(0, Math.min(time, Math.max(0, video.duration - 0.001)));
    if (Math.abs(video.currentTime - next) < SEEK_EPS) return;
    try {
      video.currentTime = next;
    } catch (e) {
      /* ignore */
    }
  }

  function paintFirstFrame(video) {
    seekVideo(video, 0.001);
    var playPromise = video.play();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise
        .then(function () {
          video.pause();
          seekVideo(video, 0.001);
          video.removeAttribute('poster');
        })
        .catch(function () {
          seekVideo(video, 0.001);
          video.removeAttribute('poster');
        });
    } else {
      try {
        video.pause();
      } catch (e) {
        /* ignore */
      }
      video.removeAttribute('poster');
    }
  }

  function scrollDistance(duration, mobile) {
    var vh = window.innerHeight || 800;
    if (mobile) {
      var byDuration = duration * vh * 0.45;
      return Math.round(Math.max(vh * 1.6, Math.min(vh * 2.6, byDuration)));
    }
    var byDurationDesk = duration * vh * 0.35;
    return Math.round(Math.max(vh * 1.35, Math.min(vh * 2.1, byDurationDesk)));
  }

  function paintPinSpacer(section) {
    var spacer = section && section.parentNode;
    if (spacer && spacer.classList && spacer.classList.contains('pin-spacer')) {
      spacer.style.setProperty('background', PIN_SPACER_BG, 'important');
      spacer.style.setProperty('background-color', PIN_SPACER_BG, 'important');
    }
  }

  function setPinLayer(section, active) {
    if (!section) return;
    section.style.zIndex = active ? '5' : '';
    var spacer = section.parentNode;
    if (spacer && spacer.classList && spacer.classList.contains('pin-spacer')) {
      spacer.style.zIndex = active ? '5' : '';
    }
  }

  function buildPageScrub(gsap, section, video, opts) {
    var proxy = { time: 0.001 };
    var endTime = Math.max(0.001, video.duration - 0.001);

    var tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        trigger: section,
        start: opts.start,
        end: function () {
          return '+=' + scrollDistance(video.duration, opts.mobile);
        },
        pin: true,
        pinSpacing: true,
        /* 1:1 with scroll so last frame lands exactly on pin release. */
        scrub: true,
        anticipatePin: 0,
        invalidateOnRefresh: true,
        onRefresh: function (self) {
          paintPinSpacer(section);
          setPinLayer(section, self.isActive);
          if (self.progress <= 0) seekVideo(video, 0.001);
          else if (self.progress >= 1) seekVideo(video, endTime);
        },
        onToggle: function (self) {
          paintPinSpacer(section);
          setPinLayer(section, self.isActive);
        },
        onLeave: function () {
          seekVideo(video, endTime);
          setPinLayer(section, false);
        },
        onLeaveBack: function () {
          seekVideo(video, 0.001);
          setPinLayer(section, false);
        }
      }
    });

    /* Settle → scrub → hold last frame (smooth pin in/out). */
    tl.to(
      proxy,
      {
        time: 0.001,
        duration: 0.08,
        onUpdate: function () {
          seekVideo(video, proxy.time);
        }
      },
      0
    );

    tl.to(
      proxy,
      {
        time: endTime,
        duration: 0.82,
        onUpdate: function () {
          seekVideo(video, proxy.time);
        }
      },
      0.08
    );

    tl.to(
      proxy,
      {
        time: endTime,
        duration: 0.1,
        onUpdate: function () {
          seekVideo(video, endTime);
        }
      },
      0.9
    );

    return tl;
  }

  function buildImmersiveScrub(gsap, scroller, track, video) {
    var proxy = { time: 0.001 };
    var endTime = Math.max(0.001, video.duration - 0.001);
    var extra = scrollDistance(video.duration, true);
    track.style.setProperty('--ana-scrub-extra', extra + 'px');

    var tl = gsap.timeline({
      defaults: { ease: 'none' },
      scrollTrigger: {
        scroller: scroller,
        trigger: track,
        start: 'top top',
        end: 'bottom bottom',
        scrub: true,
        invalidateOnRefresh: true,
        onUpdate: function (self) {
          if (self.progress > 0.04) {
            var root = scroller.closest('[data-anatomy-immersive]');
            if (root) root.classList.add('is-scrubbing');
          }
        },
        onRefresh: function (self) {
          if (self.progress <= 0) seekVideo(video, 0.001);
          else if (self.progress >= 1) seekVideo(video, endTime);
        }
      }
    });

    tl.to(
      proxy,
      {
        time: 0.001,
        duration: 0.05,
        onUpdate: function () {
          seekVideo(video, proxy.time);
        }
      },
      0
    );

    tl.to(
      proxy,
      {
        time: endTime,
        duration: 0.9,
        onUpdate: function () {
          seekVideo(video, proxy.time);
        }
      },
      0.05
    );

    tl.to(
      proxy,
      {
        time: endTime,
        duration: 0.05,
        onUpdate: function () {
          seekVideo(video, endTime);
        }
      },
      0.95
    );

    return tl;
  }

  function getImmersive() {
    return document.querySelector('[data-anatomy-immersive]');
  }

  function ensureImmersiveInBody(immersive) {
    if (!immersive) return;
    if (immersive.parentNode !== document.body) {
      document.body.appendChild(immersive);
    }
  }

  function initMobileExperience(section, gsap, ScrollTrigger, video) {
    var pin = section.querySelector('[data-anatomy-pin]');
    var frame = section.querySelector('[data-anatomy-frame]');
    var openBtn = section.querySelector('[data-anatomy-open]');
    var immersive = getImmersive();
    if (!pin || !frame || !openBtn || !immersive) return function () {};

    ensureImmersiveInBody(immersive);

    var scroller = immersive.querySelector('[data-anatomy-scroller]');
    var track = immersive.querySelector('.anatomy361-immersive__track');
    var stage = immersive.querySelector('[data-anatomy-stage]');
    var closeBtn = immersive.querySelector('[data-anatomy-close]');
    if (!scroller || !track || !stage || !closeBtn) return function () {};

    var immersiveTl = null;
    var isOpen = false;
    var scrollY = 0;

    function killImmersiveScrub() {
      if (immersiveTl) {
        if (immersiveTl.scrollTrigger) immersiveTl.scrollTrigger.kill();
        immersiveTl.kill();
        immersiveTl = null;
      }
    }

    function restorePreview() {
      if (pin && frame && frame.parentNode !== pin) {
        var playBtn = pin.querySelector('[data-anatomy-open]');
        if (playBtn) {
          pin.insertBefore(frame, playBtn);
        } else {
          pin.appendChild(frame);
        }
      }
      seekVideo(video, 0.001);
    }

    function closeImmersive() {
      if (!isOpen) return;
      isOpen = false;

      killImmersiveScrub();
      restorePreview();

      immersive.classList.remove('is-open', 'is-scrubbing');
      immersive.setAttribute('hidden', '');
      immersive.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('anatomy361-is-open');

      window.scrollTo(0, scrollY);
      ScrollTrigger.refresh();
    }

    function openImmersive() {
      if (isOpen) return;
      isOpen = true;

      scrollY = window.scrollY || window.pageYOffset || 0;

      stage.appendChild(frame);
      seekVideo(video, 0.001);
      paintFirstFrame(video);

      immersive.removeAttribute('hidden');
      immersive.setAttribute('aria-hidden', 'false');
      immersive.classList.add('is-open');
      immersive.classList.remove('is-scrubbing');
      document.documentElement.classList.add('anatomy361-is-open');

      scroller.scrollTop = 0;

      try {
        var p = video.play();
        if (p && typeof p.then === 'function') {
          p.then(function () {
            video.pause();
            seekVideo(video, 0.001);
          }).catch(function () {});
        }
      } catch (e) {
        /* ignore */
      }

      window.requestAnimationFrame(function () {
        killImmersiveScrub();
        immersiveTl = buildImmersiveScrub(gsap, scroller, track, video);
        ScrollTrigger.refresh();
        closeBtn.focus({ preventScroll: true });
      });
    }

    function onOpenClick(e) {
      e.preventDefault();
      openImmersive();
    }

    function onCloseClick(e) {
      e.preventDefault();
      closeImmersive();
    }

    function onKey(e) {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        closeImmersive();
      }
    }

    openBtn.addEventListener('click', onOpenClick);
    closeBtn.addEventListener('click', onCloseClick);
    document.addEventListener('keydown', onKey);

    return function cleanup() {
      closeImmersive();
      openBtn.removeEventListener('click', onOpenClick);
      closeBtn.removeEventListener('click', onCloseClick);
      document.removeEventListener('keydown', onKey);
      restorePreview();
    };
  }

  function initSection(section, gsap, ScrollTrigger) {
    var pin = section.querySelector('[data-anatomy-pin]');
    var video = section.querySelector('[data-anatomy-video]');
    if (!pin || !video) return;

    if (section._anatomy361Ctx) {
      section._anatomy361Ctx.revert();
    }
    section._anatomy361Ctx = null;

    video.pause();
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');

    onceReady(video, function () {
      paintFirstFrame(video);

      var ctx = gsap.context(function () {
        var mm = gsap.matchMedia();

        mm.add('(prefers-reduced-motion: reduce)', function () {
          seekVideo(video, 0);
          var openBtn = section.querySelector('[data-anatomy-open]');
          if (openBtn) openBtn.hidden = true;
        });

        mm.add(MOBILE_MQ + ' and (prefers-reduced-motion: no-preference)', function () {
          return initMobileExperience(section, gsap, ScrollTrigger, video);
        });

        mm.add(DESKTOP_MQ + ' and (prefers-reduced-motion: no-preference)', function () {
          var immersive = getImmersive();
          if (immersive) {
            immersive.classList.remove('is-open', 'is-scrubbing');
            immersive.setAttribute('hidden', '');
            immersive.setAttribute('aria-hidden', 'true');
          }
          document.documentElement.classList.remove('anatomy361-is-open');

          var pinEl = section.querySelector('[data-anatomy-pin]');
          var frame = section.querySelector('[data-anatomy-frame]');
          if (pinEl && frame && frame.parentNode !== pinEl) {
            var playBtn = pinEl.querySelector('[data-anatomy-open]');
            if (playBtn) pinEl.insertBefore(frame, playBtn);
            else pinEl.appendChild(frame);
          }

          var tl = buildPageScrub(gsap, section, video, {
            start: 'top top',
            mobile: false
          });
          return function () {
            if (tl.scrollTrigger) tl.scrollTrigger.kill();
            tl.kill();
          };
        });
      }, section);

      section._anatomy361Ctx = ctx;
      ScrollTrigger.refresh();
      paintPinSpacer(section);
    });
  }

  function boot() {
    whenGsap(function (gsap, ScrollTrigger) {
      gsap.registerPlugin(ScrollTrigger);

      if (!ST_CONFIGURED) {
        ST_CONFIGURED = true;
        ScrollTrigger.config({ ignoreMobileResize: true });
      }

      var immersive = getImmersive();
      ensureImmersiveInBody(immersive);

      document.querySelectorAll('[data-anatomy361]').forEach(function (section) {
        initSection(section, gsap, ScrollTrigger);
      });

      if (!BOOTED) {
        BOOTED = true;
        window.addEventListener(
          'load',
          function () {
            ScrollTrigger.refresh();
          },
          { once: true }
        );
        window.addEventListener('orientationchange', function () {
          window.setTimeout(function () {
            ScrollTrigger.refresh();
          }, 300);
        });
      }
    });
  }

  ready(boot);

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target;
    if (!root || !root.querySelector) return;
    if (!root.querySelector('[data-anatomy361]') && !(root.matches && root.matches('[data-anatomy361]'))) {
      return;
    }
    boot();
  });
})();
